import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';

class ImageAnalysisService {
  constructor() {
    this.geminiClient = null;
    this.initializeService();
  }

  initializeService() {
    const geminiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (geminiKey) {
      try {
        this.geminiClient = new GoogleGenerativeAI(geminiKey);
        console.log('✅ Image Analysis Service with Gemini initialized');
      } catch (error) {
        console.error('❌ Image Analysis Service initialization failed:', error);
      }
    }
  }

  isAvailable() {
    return this.geminiClient !== null;
  }

  /**
   * Analyze uploaded image and extract educational content
   */
  async analyzeEducationalImage(imageBase64, gradeLevel, language = 'English') {
    if (!this.isAvailable()) {
      throw new Error('Image Analysis Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('🖼️ Analyzing educational image...', { autoClose: 4000 });

      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Analyze this educational image and provide comprehensive analysis for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "imageDescription": "Detailed description of what you see in the image",
  "subject": "Primary subject area (Math, Science, History, etc.)",
  "topic": "Main topic from the image",
  "grade": ${gradeLevel},
  "language": "${language}",
  "keyPoints": ["Key point 1", "Key point 2", "Key point 3"],
  "concepts": ["Concept 1", "Concept 2", "Concept 3"],
  "questions": [
    "What do you see in this image?",
    "How does this relate to your studies?",
    "What can you learn from this?"
  ],
  "difficulty": "easy",
  "learningObjectives": ["Learn about X", "Understand Y", "Recognize Z"],
  "vocabulary": ["Important term 1", "Important term 2", "Important term 3"],
  "realWorldConnections": ["How this applies in real life"],
  "furtherLearning": ["What students can explore next"]
}

Requirements:
- Analyze all visible text, diagrams, charts, or educational content
- Age-appropriate analysis for Grade ${gradeLevel}
- Clear explanations in ${language}
- Focus on educational value
- Return ONLY valid JSON`;

      const imagePart = {
        inlineData: {
          data: imageBase64.replace(/^data:image\/[^;]+;base64,/, ''),
          mimeType: "image/jpeg"
        }
      };

      const response = await model.generateContent([prompt, imagePart]);
      let contentText = response.response.text().trim();

      // Clean up the response
      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
      } catch (jsonError) {
        console.warn('Image analysis JSON parsing failed, using fallback');
        parsedContent = this.generateFallbackAnalysis(gradeLevel, language);
      }

      // Validate and ensure all required fields
      const validatedAnalysis = {
        imageDescription: parsedContent.imageDescription || "Educational content from uploaded image",
        subject: parsedContent.subject || "General Education",
        topic: parsedContent.topic || "Image Analysis",
        grade: gradeLevel,
        language: language,
        keyPoints: Array.isArray(parsedContent.keyPoints) ? parsedContent.keyPoints : [
          "Visual content analysis",
          "Educational elements identification",
          "Learning from images"
        ],
        concepts: Array.isArray(parsedContent.concepts) ? parsedContent.concepts : [
          "Visual learning",
          "Image interpretation",
          "Educational analysis"
        ],
        questions: Array.isArray(parsedContent.questions) ? parsedContent.questions : [
          "What do you see in this image?",
          "What educational content can you identify?",
          "How does this relate to your studies?"
        ],
        difficulty: ['easy', 'medium', 'hard'].includes(parsedContent.difficulty) ? parsedContent.difficulty : 'medium',
        learningObjectives: Array.isArray(parsedContent.learningObjectives) ? parsedContent.learningObjectives : [
          "Analyze visual content",
          "Extract educational information",
          "Connect images to learning"
        ],
        vocabulary: Array.isArray(parsedContent.vocabulary) ? parsedContent.vocabulary : [
          "Analysis",
          "Education",
          "Visual learning"
        ],
        realWorldConnections: Array.isArray(parsedContent.realWorldConnections) ? parsedContent.realWorldConnections : [
          "Images help us understand concepts better"
        ],
        furtherLearning: Array.isArray(parsedContent.furtherLearning) ? parsedContent.furtherLearning : [
          "Explore related topics",
          "Practice image analysis",
          "Connect to textbook content"
        ]
      };

      return {
        success: true,
        data: {
          ...validatedAnalysis,
          model: 'gemini-2.5-flash',
          source: 'Image Analysis Service',
          format: 'educational_image_analysis',
          analysisTimestamp: new Date().toISOString()
        }
      };

    } catch (error) {
      console.error('Image analysis failed:', error);
      throw new Error(`Image analysis failed: ${error.message}`);
    }
  }

  /**
   * Analyze textbook page or chapter image
   */
  async analyzeTextbookPage(imageBase64, gradeLevel, subject, language = 'English') {
    if (!this.isAvailable()) {
      throw new Error('Image Analysis Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('📚 Analyzing textbook page...', { autoClose: 4000 });

      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Analyze this textbook page/chapter image for ${subject} Grade ${gradeLevel} in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "pageType": "chapter_page or exercise_page or diagram_page",
  "subject": "${subject}",
  "chapterTitle": "Chapter title if visible",
  "topics": ["Topic 1", "Topic 2"],
  "textContent": "Any visible text content (summarized)",
  "diagrams": ["Description of diagram 1", "Description of diagram 2"],
  "exercises": ["Exercise 1", "Exercise 2"],
  "keyFormulas": ["Formula 1", "Formula 2"],
  "definitions": {"Term 1": "Definition 1", "Term 2": "Definition 2"},
  "grade": ${gradeLevel},
  "language": "${language}",
  "learningOutcomes": ["What students should learn"],
  "difficulty": "easy",
  "prerequisites": ["What students should know before this"],
  "practiceQuestions": ["Question based on this page"],
  "crossReferences": ["Related topics or chapters"]
}

Requirements:
- Extract all visible text, formulas, and diagrams
- Identify educational elements specific to ${subject}
- Age-appropriate for Grade ${gradeLevel}
- Clear analysis in ${language}
- Return ONLY valid JSON`;

      const imagePart = {
        inlineData: {
          data: imageBase64.replace(/^data:image\/[^;]+;base64,/, ''),
          mimeType: "image/jpeg"
        }
      };

      const response = await model.generateContent([prompt, imagePart]);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
      } catch (jsonError) {
        console.warn('Textbook analysis JSON parsing failed, using fallback');
        parsedContent = this.generateFallbackTextbookAnalysis(subject, gradeLevel, language);
      }

      // Validate and ensure all required fields
      const validatedAnalysis = {
        pageType: parsedContent.pageType || "educational_page",
        subject: subject,
        chapterTitle: parsedContent.chapterTitle || "Chapter Analysis",
        topics: Array.isArray(parsedContent.topics) ? parsedContent.topics : [subject + " topics"],
        textContent: parsedContent.textContent || "Educational content from textbook page",
        diagrams: Array.isArray(parsedContent.diagrams) ? parsedContent.diagrams : ["Visual elements"],
        exercises: Array.isArray(parsedContent.exercises) ? parsedContent.exercises : ["Practice problems"],
        keyFormulas: Array.isArray(parsedContent.keyFormulas) ? parsedContent.keyFormulas : [],
        definitions: typeof parsedContent.definitions === 'object' ? parsedContent.definitions : {},
        grade: gradeLevel,
        language: language,
        learningOutcomes: Array.isArray(parsedContent.learningOutcomes) ? parsedContent.learningOutcomes : [
          "Understand key concepts",
          "Apply knowledge to problems"
        ],
        difficulty: ['easy', 'medium', 'hard'].includes(parsedContent.difficulty) ? parsedContent.difficulty : 'medium',
        prerequisites: Array.isArray(parsedContent.prerequisites) ? parsedContent.prerequisites : ["Basic understanding"],
        practiceQuestions: Array.isArray(parsedContent.practiceQuestions) ? parsedContent.practiceQuestions : [
          "What are the main concepts on this page?"
        ],
        crossReferences: Array.isArray(parsedContent.crossReferences) ? parsedContent.crossReferences : ["Related topics"]
      };

      return {
        success: true,
        data: {
          ...validatedAnalysis,
          model: 'gemini-2.5-flash',
          source: 'Textbook Analysis Service',
          format: 'textbook_page_analysis',
          analysisTimestamp: new Date().toISOString()
        }
      };

    } catch (error) {
      console.error('Textbook analysis failed:', error);
      throw new Error(`Textbook analysis failed: ${error.message}`);
    }
  }

  /**
   * Extract text from image using OCR capabilities
   */
  async extractTextFromImage(imageBase64, language = 'English') {
    if (!this.isAvailable()) {
      throw new Error('Image Analysis Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('📖 Extracting text from image...', { autoClose: 3000 });

      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Extract all visible text from this image in ${language}. Return only the extracted text, maintaining the original structure and formatting as much as possible. If there are mathematical formulas, equations, or special symbols, describe them clearly.`;

      const imagePart = {
        inlineData: {
          data: imageBase64.replace(/^data:image\/[^;]+;base64,/, ''),
          mimeType: "image/jpeg"
        }
      };

      const response = await model.generateContent([prompt, imagePart]);
      const extractedText = response.response.text().trim();

      return {
        success: true,
        data: {
          extractedText: extractedText,
          language: language,
          model: 'gemini-2.5-flash',
          source: 'OCR via Image Analysis Service',
          format: 'extracted_text',
          extractionTimestamp: new Date().toISOString()
        }
      };

    } catch (error) {
      console.error('Text extraction failed:', error);
      throw new Error(`Text extraction failed: ${error.message}`);
    }
  }

  generateFallbackAnalysis(gradeLevel, language) {
    return {
      imageDescription: "Educational content detected in uploaded image",
      subject: "General Education",
      topic: "Image-based Learning",
      grade: gradeLevel,
      language: language,
      keyPoints: [
        "Visual learning is important",
        "Images contain educational information",
        "Analysis helps understanding"
      ],
      concepts: [
        "Visual analysis",
        "Educational content identification",
        "Learning from images"
      ],
      questions: [
        "What educational content do you see?",
        "How can this image help in learning?",
        "What questions does this image raise?"
      ],
      difficulty: "medium",
      learningObjectives: [
        "Develop visual analysis skills",
        "Extract information from images",
        "Connect visuals to learning"
      ],
      vocabulary: ["Analysis", "Education", "Visual", "Learning"],
      realWorldConnections: ["Images are everywhere and contain information"],
      furtherLearning: ["Practice analyzing different types of educational images"]
    };
  }

  generateFallbackTextbookAnalysis(subject, gradeLevel, language) {
    return {
      pageType: "educational_page",
      subject: subject,
      chapterTitle: `${subject} Chapter`,
      topics: [`${subject} concepts`],
      textContent: "Educational content from textbook page",
      diagrams: ["Educational diagrams and illustrations"],
      exercises: ["Practice problems and exercises"],
      keyFormulas: [],
      definitions: {},
      grade: gradeLevel,
      language: language,
      learningOutcomes: ["Understand chapter concepts", "Apply knowledge"],
      difficulty: "medium",
      prerequisites: ["Previous chapter knowledge"],
      practiceQuestions: [`What are the main ${subject} concepts here?`],
      crossReferences: ["Related chapters and topics"]
    };
  }
}

export default ImageAnalysisService;